<template>
  <div>
    <b-container>
      <div class="mt-4 d-flex align-items-start justify-content-between">
        <div>
          <h1 class="page-title">{{ $t('Farmers') }}</h1>
          <h2 class="page-subtitle">{{ totalCount }}
            {{ totalCount === 1 ? $t('farmer') + $t('saved') : $t('farmers') + $t('saved.s') }}</h2>
        </div>
        <div class="d-flex">
          <b-button class="ph-btn mr-2 dl-btn" variant="secondary" @click="clickOnGlobalExportBtn">
            <div class="d-flex" v-if="!isLoading">{{ $t('Export') }} <i class="ph-custom ph-file-xls"/></div>
            <b-spinner v-else label="Loading..." small></b-spinner>
          </b-button>
          <b-button class="ph-btn" variant="primary" @click="clickOnCreateBtn" v-if="allowCreate">
            {{ $t('Add2') }} {{ $t('a') }} {{ $t('farmer') }} <i class="ph-custom ph-user-plus-fill ml-1"/>
          </b-button>
        </div>
      </div>

      <b-card class="mt-2 mb-4 shadow-sm">
        <b122-table
          ref="B122TableFarmer"
          identifier="B122TableFarmer"
          get-b122-table-results-url="/farmer/getB122TableResults"
          :fields="tableFields"
          :editItem="allowEdit"
          :deleteItem="allowDelete"
          :empty-title="$t('farmer.emptyTitle').toString()"
          :empty-text="$t('farmer.emptyText').toString()"
          @setTotalCount="setTotalCount"
          @clickOnShowBtn="clickOnShowBtn"
          @clickOnEditBtn="clickOnEditBtn"
          @clickOnDeleteBtn="clickOnDeleteBtn">

          <template #cell(technician)="data">
            <div class="textual-link" v-if="!isTechnician && !data.item.technician.isDefaultTechnician"
                 @click="goToTechnician(data.item.technician.id )">
              {{ data.item.technician.tostring }}
            </div>
            <div class="" v-else>{{ data.item.technician.tostring }}</div>
          </template>

          <template #cell(structure)="data">
            <div class="textual-link" v-if="allowLinkStructure&&!data.item.structure.isDefaultStructure"
                 @click="goToStructure(data.item.structure.id)">
              {{ data.item.structure.tostring }}
            </div>
            <div v-else>{{ data.item.structure.tostring }}</div>
          </template>

          <template #cell(lastAuditDate)="data">
            <div v-if="data.item.lastAuditDate !== null">{{
                new Date(data.item.lastAuditDate) | moment("DD/MM/YYYY")
              }}
            </div>
            <div v-else>-</div>
          </template>

          <template #cell(ongoingAudit)="data">
            <div v-if="data.item.hasOngoingAudit && data.item.ongoingAudit !== null">
              <div v-if="data.item.ongoingAudit.realisationDate > new Date().getTime() - 370 * 24 * 60 * 60 * 1000">
                <i class="ph-custom ph-check-square-fill main-color d-flex align-items-center"/>
              </div>
              <div v-else>
                <i class="ph-custom ph-check-square-fill secondary-color d-flex align-items-center"/>
              </div>
            </div>

            <div v-else-if="data.item.hasInProgressAudit">
              <button class="action-btn download btn-secondary" @click="clickOnCreateAuditBtn(data.item.id)" :disabled="!canCreateAudit" :title="canCreateAudit ? '' : $t('Only.technician.validate')">
                <i class="ph-custom ph-folder-notch-plus-fill d-flex align-items-center"/>
              </button>
            </div>

            <div v-else-if="!data.item.hasInProgressAudit && !data.item.hasOngoingAudit">
              <div v-if="!data.item.technician.isDefaultTechnician">
                <button class="action-btn edit btn-primary" @click="clickOnCreateAuditBtn(data.item.id)" :disabled="!canCreateAudit" :title="canCreateAudit ? '' : $t('Only.technician.create')">
                  <i class="ph-custom ph-folder-notch-plus-fill d-flex align-items-center"/>
                </button>
              </div>
              <div v-else>
                <i class="ph-custom ph-x-square-fill main-red-color d-flex align-items-center"/>
              </div>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="actions-container text-right actions-container-custom">
              <button class="action-btn download mr-1" @click="clickOnDownloadBtn(data.item)">
                <i v-if="!data.item.isLoadingExcel" class="ph-custom ph-file-xls"/>
                <b-spinner v-else label="Loading..." small></b-spinner>
              </button>
              <button class="action-btn edit mr-1" @click="clickOnEditBtn(data)" v-if="allowEdit">
                <i class="ph-custom ph-pencil-simple-line-fill"/>
              </button>
              <button class="action-btn delete" @click="clickOnDeleteBtn(data)" v-if="allowDelete">
                <i class="ph-custom ph-trash-fill"/>
              </button>
            </div>
          </template>

        </b122-table>
      </b-card>
    </b-container>

    <Item-delete-modal
      :beginsWithVoyel="true"
      :isFeminine="false"
      :selectedItem="selectedItem"
      controllerName="farmer"
      itemSingularName="farmer"
      itemSingularNameUpp="Farmer"
      @didDeleteItem="didDeleteItem()">
    </Item-delete-modal>
  </div>
</template>

<script>

import B122Table from '../../components/b122/B122Table'
import ItemDeleteModal from '../../components/b122/ItemDeleteModal'
import api from '../../services/api'

export default {

  name: 'FarmerIndex',

  components: {
    B122Table,
    ItemDeleteModal
  },

  data () {
    let isAuthorized = this.$store.getters.isAdmin || this.$store.getters.isStructure || this.$store.getters.isTechnician || this.$store.getters.isSuperAdmin
    return {
      totalCount: 0,
      allowCreate: isAuthorized,
      allowDelete: this.$store.getters.isAdmin || this.$store.getters.isSuperAdmin,
      allowEdit: isAuthorized,
      selectedItem: null,
      isTechnician: this.$store.getters.isTechnician,
      allowLinkStructure: this.$store.getters.isAdmin || this.$store.getters.isSuperAdmin,
      isLoading: false,
      canCreateAudit: this.$store.getters.isTechnician
    }
  },

  computed: {

    tableFields () {
      return [
        {
          label: '',
          key: 'show',
          sortable: false,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.name.elevage'),
          key: 'farmName',
          sortable: true,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.num.Cheptel'),
          key: 'cheptelNumber',
          sortable: true,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.city'),
          key: 'city',
          sortable: true,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.technician'),
          key: 'technician',
          sortable: true,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.structure'),
          key: 'structure',
          sortable: true,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.last.audit'),
          key: 'lastAuditDate',
          sortable: false,
          tdClass: 'max-width-cell'
        },
        {
          label: this.$i18n.t('farmer.State'),
          key: 'ongoingAudit',
          sortable: false,
          tdClass: 'max-width-cell justify-content-center d-flex align-items-center empty-row'
        },
        {
          label: '',
          key: 'actions',
          tdClass: 'max-width-cell'
        }
      ]
    }

  },

  methods: {

    setTotalCount (totalCount) {
      this.totalCount = totalCount
    },

    clickOnCreateBtn () {
      this.$router.push({
        name: 'FarmerCreate'
      })
    },

    clickOnShowBtn (row) {
      this.$router.push({
        name: 'FarmerShow',
        params: {
          farmerId: row.item.id
        }
      })
    },

    goToTechnician (id) {
      this.$router.push({
        name: 'TechnicianShow',
        params: {
          techId: id
        }
      })
    },

    goToStructure (id) {
      this.$router.push({
        name: 'StructureShow',
        params: {
          structureId: id
        }
      })
    },

    clickOnEditBtn (row) {
      this.$router.push({
        name: 'FarmerEdit',
        params: {
          farmerId: row.item.id
        }
      })
    },

    clickOnDeleteBtn (row) {
      this.selectedItem = row.item
      this.$bvModal.show('item-delete-modal')
    },

    didDeleteItem () {
      this.selectedItem = null
      this.$refs.B122TableFarmer.refreshTable()
    },

    clickOnDownloadBtn (row) {
      const params = new URLSearchParams()
      params.append('farmerId', row.id)
      this.$set(row, 'isLoadingExcel', true)
      api.post('/farmer/downloadExcel', params, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/vnd.ms-excel'
        }
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `farmer-${row.id}-${new Date().toLocaleDateString('fr-FR')}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        this.$set(row, 'isLoadingExcel', false)
      })
    },

    clickOnGlobalExportBtn () {
      let tableState = this.$refs.B122TableFarmer.tableStateIndex()
      let formData = new FormData()
      formData.append('sortBy', tableState.sortBy)
      formData.append('sortDesc', tableState.sortDesc)

      if (tableState.search && tableState.search.trim() !== '') {
        formData.append('search', tableState.search.trim())
      }

      formData.append('filters', JSON.stringify(tableState.filters))

      this.isLoading = true
      api.post('/farmer/downloadGlobalExcel', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/vnd.ms-excel'
        }
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `global-export-${new Date().toLocaleDateString('fr-FR')}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        this.isLoading = false
      })
    },

    clickOnCreateAuditBtn (farmerId) {
      this.$router.push({
        name: 'AuditCreate',
        params: {
          farmerId: farmerId
        }
      })
    }

  }
}
</script>

<style scoped>
.actions-container-custom {
  display: flex;
  align-content: flex-end;
  justify-content: flex-end;
  align-items: flex-end;
}

.download {
  background-color: var(--main-yellow-light);
  border-color: var(--main-yellow-light);
  color: var(--main-yellow-dark);
}

.download:hover {
  background-color: var(--main-yellow-medium);
}

.dl-btn {
  width: 200px;
}
</style>



// WEBPACK FOOTER //
// src/views/farmer/FarmerIndex.vue